import React, { useState, useEffect } from 'react';
import {
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Download,
  CheckCircle2,
  Circle,
  AlertTriangle,
  ChevronDown,
  Info,
  Pencil,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format, isToday, isTomorrow, isPast } from 'date-fns';

const typeConfig = {
  OFFICIAL: { label: 'OFFICIAL', accent: 'border-l-blue-600', dot: 'bg-blue-600' },
  MOCK: { label: 'MOCK', accent: 'border-l-amber-600', dot: 'bg-amber-600' },
  PERSONAL: { label: 'PERSONAL', accent: 'border-l-slate-400', dot: 'bg-slate-400' },
};

const DeadlinesPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deadlines, setDeadlines] = useState([]);
  const [clusterWarnings, setClusterWarnings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deadlineToDelete, setDeadlineToDelete] = useState(null);
  const [filter, setFilter] = useState({ type: 'all', projectType: 'all' });

  const [formData, setFormData] = useState({
    label: '',
    type: 'PERSONAL',
    projectId: '',
    dueAt: new Date(),
    allDay: true,
    description: '',
    time: '23:59'
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [deadlinesRes, projectsRes] = await Promise.all([
        api.get('/deadlines'),
        api.get('/projects')
      ]);
      setDeadlines(deadlinesRes.data.deadlines || []);
      setClusterWarnings(deadlinesRes.data.clusterWarnings || []);
      setProjects(projectsRes.data.projects || []);
    } catch (err) {
      toast.error('Failed to load deadlines');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeadline = async (e) => {
    e.preventDefault();
    if (!formData.label.trim()) { toast.error('Please enter a deadline name'); return; }
    try {
      let dueAt = new Date(formData.dueAt);
      if (!formData.allDay && formData.time) {
        const [hours, minutes] = formData.time.split(':');
        dueAt.setHours(parseInt(hours), parseInt(minutes));
      }
      if (editingDeadline) {
        const res = await api.patch(`/deadlines/${editingDeadline.id}`, {
          ...formData, dueAt: dueAt.toISOString(),
          projectId: formData.projectId && formData.projectId !== 'none' ? formData.projectId : null
        });
        if (res.data.warning) toast.warning(res.data.warning);
        await fetchData();
        setDialogOpen(false); setEditingDeadline(null); resetForm();
        toast.success('Deadline updated!');
      } else {
        const res = await api.post('/deadlines', {
          ...formData, dueAt: dueAt.toISOString(),
          projectId: formData.projectId && formData.projectId !== 'none' ? formData.projectId : null
        });
        if (res.data.warning) toast.warning(res.data.warning);
        await fetchData();
        setDialogOpen(false); resetForm();
        toast.success('Deadline created!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${editingDeadline ? 'update' : 'create'} deadline`);
    }
  };

  const resetForm = () => {
    setFormData({ label: '', type: 'PERSONAL', projectId: '', dueAt: new Date(), allDay: true, description: '', time: '23:59' });
  };

  const handleEditDeadline = (deadline) => {
    const dueDate = new Date(deadline.dueAt);
    const h = dueDate.getHours().toString().padStart(2, '0');
    const m = dueDate.getMinutes().toString().padStart(2, '0');
    setEditingDeadline(deadline);
    setFormData({ label: deadline.label, type: deadline.type, projectId: deadline.projectId || '', dueAt: dueDate, allDay: deadline.allDay, description: deadline.description || '', time: `${h}:${m}` });
    setDialogOpen(true);
  };

  const handleDeleteDeadline = async () => {
    if (!deadlineToDelete) return;
    try {
      await api.delete(`/deadlines/${deadlineToDelete.id}`);
      await fetchData();
      setDeleteDialogOpen(false); setDeadlineToDelete(null);
      toast.success('Deadline deleted!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete deadline');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) { setEditingDeadline(null); resetForm(); }
  };

  const handleToggleComplete = async (deadline) => {
    try {
      const res = await api.patch(`/deadlines/${deadline.id}/toggle`);
      setDeadlines(deadlines.map(d => d.id === deadline.id ? res.data.deadline : d));
    } catch (err) {
      toast.error('Failed to update deadline');
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await api.get('/ical/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ib-planner-deadlines.ics');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Calendar exported');
    } catch (err) {
      toast.error('Failed to export calendar');
    }
  };

  const filteredDeadlines = deadlines.filter(d => {
    if (filter.type !== 'all' && d.type !== filter.type) return false;
    if (filter.projectType !== 'all' && d.project?.type !== filter.projectType) return false;
    return true;
  });

  const grouped = {
    overdue: filteredDeadlines.filter(d => !d.completed && isPast(new Date(d.dueAt)) && !isToday(new Date(d.dueAt))),
    today: filteredDeadlines.filter(d => isToday(new Date(d.dueAt))),
    tomorrow: filteredDeadlines.filter(d => isTomorrow(new Date(d.dueAt))),
    upcoming: filteredDeadlines.filter(d => {
      const date = new Date(d.dueAt);
      return !isPast(date) && !isToday(date) && !isTomorrow(date);
    }),
    completed: filteredDeadlines.filter(d => d.completed)
  };

  const stats = {
    total: deadlines.filter(d => !d.completed).length,
    official: deadlines.filter(d => d.type === 'OFFICIAL' && !d.completed).length,
    mock: deadlines.filter(d => d.type === 'MOCK' && !d.completed).length,
    personal: deadlines.filter(d => d.type === 'PERSONAL' && !d.completed).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading deadlines...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="deadlines-page">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border pb-5 mb-6">
        <div>
          <p className="label-swiss mb-1">Timeline</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Deadlines</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportAll} data-testid="export-all-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Export iCal
          </Button>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-deadline-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add Deadline
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingDeadline ? 'Edit Deadline' : 'Add Deadline'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateDeadline} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="label" className="text-xs font-medium">Deadline Name *</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g., Mock EE Draft 1 Submission"
                    data-testid="deadline-label-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Deadline Type</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(typeConfig).map(([t, cfg]) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setFormData({ ...formData, type: t })}
                        className={`p-2.5 border text-left transition-colors ${
                          formData.type === t ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/30'
                        }`}
                        data-testid={`deadline-type-${t}`}
                      >
                        <div className={`w-2 h-2 ${cfg.dot} mb-1.5`} />
                        <p className="text-xs font-semibold uppercase tracking-wide">{cfg.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Linked Project</Label>
                  <Select value={formData.projectId} onValueChange={(v) => setFormData({ ...formData, projectId: v })}>
                    <SelectTrigger data-testid="deadline-project-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.title || p.subject || p.type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-sm h-9" data-testid="deadline-date-picker">
                        <CalendarIcon className="w-3.5 h-3.5 mr-2" />
                        {formData.dueAt ? format(formData.dueAt, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                      <Calendar
                        mode="single"
                        selected={formData.dueAt}
                        onSelect={(d) => setFormData({ ...formData, dueAt: d || new Date() })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="allDay"
                      checked={formData.allDay}
                      onCheckedChange={(c) => setFormData({ ...formData, allDay: c })}
                      data-testid="deadline-allday-checkbox"
                    />
                    <Label htmlFor="allDay" className="text-xs">All day</Label>
                  </div>
                  {!formData.allDay && (
                    <div className="flex-1">
                      <Input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="h-9 text-sm"
                        data-testid="deadline-time-input"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="description" className="text-xs font-medium">Notes</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Submission instructions, reminders..."
                    rows={2}
                    data-testid="deadline-description-input"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button type="button" variant="outline" size="sm" onClick={() => handleDialogClose(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" data-testid="deadline-submit-btn">
                    {editingDeadline ? 'Update Deadline' : 'Create Deadline'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 border border-border mb-5">
        {[
          { label: 'All Active', value: stats.total, active: filter.type === 'all', onClick: () => setFilter({ ...filter, type: 'all' }) },
          { label: 'Official', value: stats.official, active: filter.type === 'OFFICIAL', onClick: () => setFilter({ ...filter, type: 'OFFICIAL' }) },
          { label: 'Mock', value: stats.mock, active: filter.type === 'MOCK', onClick: () => setFilter({ ...filter, type: 'MOCK' }) },
          { label: 'Personal', value: stats.personal, active: filter.type === 'PERSONAL', onClick: () => setFilter({ ...filter, type: 'PERSONAL' }) },
        ].map((s, i) => (
          <button
            key={s.label}
            onClick={s.onClick}
            className={`py-4 px-4 text-left transition-colors ${i > 0 ? 'border-l border-border' : ''} ${s.active ? 'bg-foreground' : 'hover:bg-muted/50'}`}
          >
            <p className={`text-2xl font-bold font-mono ${s.active ? 'text-white' : 'text-foreground'}`}>{s.value}</p>
            <p className={`text-[10px] uppercase tracking-[0.12em] font-semibold mt-0.5 ${s.active ? 'text-white/60' : 'text-muted-foreground'}`}>
              {s.label}
            </p>
          </button>
        ))}
      </div>

      {/* Cluster Warnings */}
      {clusterWarnings.length > 0 && (
        <div className="space-y-2 mb-5">
          {clusterWarnings.map((warning, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 px-4 py-3 border ${
                warning.severity === 'high'
                  ? 'border-destructive/40 bg-destructive/5'
                  : 'border-amber-200 bg-amber-50'
              }`}
            >
              {warning.severity === 'high' ? (
                <AlertTriangle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
              ) : (
                <Info className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
              )}
              <p className={`text-xs ${warning.severity === 'high' ? 'text-destructive' : 'text-amber-800'}`}>
                <strong>{warning.type === 'busy_week' ? 'Busy Week:' : 'Heads up:'}</strong> {warning.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="filter-project-btn">
              {filter.projectType === 'all' ? 'All Projects' : filter.projectType}
              <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setFilter({ ...filter, projectType: 'all' })}>All Projects</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilter({ ...filter, projectType: 'EE' })}>EE Only</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilter({ ...filter, projectType: 'IA' })}>IA Only</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilter({ ...filter, projectType: 'TOK_ESSAY' })}>TOK Only</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {filter.type !== 'all' && (
          <button
            onClick={() => setFilter({ ...filter, type: 'all' })}
            className="text-xs text-muted-foreground hover:text-foreground uppercase tracking-wide font-medium"
          >
            Clear filter ×
          </button>
        )}
      </div>

      {deadlines.length === 0 ? (
        <div className="border border-border py-16 text-center">
          <CalendarIcon className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
          <h3 className="font-semibold text-foreground mb-1">No deadlines yet</h3>
          <p className="text-sm text-muted-foreground mb-5">Add your first deadline to start tracking your IB work</p>
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add Your First Deadline
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.overdue.length > 0 && (
            <DeadlineSection title="Overdue" count={grouped.overdue.length} variant="overdue"
              deadlines={grouped.overdue} onToggle={handleToggleComplete} onEdit={handleEditDeadline}
              onDelete={(d) => { setDeadlineToDelete(d); setDeleteDialogOpen(true); }} />
          )}
          {grouped.today.length > 0 && (
            <DeadlineSection title="Today" count={grouped.today.length} variant="today"
              deadlines={grouped.today} onToggle={handleToggleComplete} onEdit={handleEditDeadline}
              onDelete={(d) => { setDeadlineToDelete(d); setDeleteDialogOpen(true); }} />
          )}
          {grouped.tomorrow.length > 0 && (
            <DeadlineSection title="Tomorrow" count={grouped.tomorrow.length}
              deadlines={grouped.tomorrow} onToggle={handleToggleComplete} onEdit={handleEditDeadline}
              onDelete={(d) => { setDeadlineToDelete(d); setDeleteDialogOpen(true); }} />
          )}
          {grouped.upcoming.length > 0 && (
            <DeadlineSection title="Upcoming" count={grouped.upcoming.length}
              deadlines={grouped.upcoming} onToggle={handleToggleComplete} onEdit={handleEditDeadline}
              onDelete={(d) => { setDeadlineToDelete(d); setDeleteDialogOpen(true); }} />
          )}
          {grouped.completed.length > 0 && (
            <DeadlineSection title="Completed" count={grouped.completed.length} variant="completed"
              deadlines={grouped.completed} onToggle={handleToggleComplete} onEdit={handleEditDeadline}
              onDelete={(d) => { setDeadlineToDelete(d); setDeleteDialogOpen(true); }} />
          )}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deadline</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deadlineToDelete?.label}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeadlineToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDeadline} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const variantStyles = {
  overdue: { header: 'text-destructive', border: 'border-destructive/30', bg: '' },
  today: { header: 'text-amber-700', border: 'border-amber-300', bg: '' },
  completed: { header: 'text-muted-foreground', border: 'border-border', bg: '' },
  default: { header: 'text-foreground', border: 'border-border', bg: '' },
};

const DeadlineSection = ({ title, count, deadlines, onToggle, onEdit, onDelete, variant }) => {
  const vs = variantStyles[variant] || variantStyles.default;
  return (
    <section>
      <div className="flex items-center gap-2 pb-2 border-b border-border mb-0">
        <h2 className={`text-xs font-semibold uppercase tracking-[0.1em] ${vs.header}`}>{title}</h2>
        <span className="font-mono text-[11px] text-muted-foreground">{count}</span>
      </div>
      <div className={`border border-t-0 ${vs.border} divide-y divide-border`}>
        {deadlines.map((deadline) => (
          <DeadlineRow
            key={deadline.id}
            deadline={deadline}
            onToggle={onToggle}
            onEdit={onEdit}
            onDelete={onDelete}
            variant={variant}
          />
        ))}
      </div>
    </section>
  );
};

const DeadlineRow = ({ deadline, onToggle, onEdit, onDelete, variant }) => {
  const cfg = typeConfig[deadline.type] || typeConfig.PERSONAL;

  return (
    <div className={`flex items-center gap-4 px-4 py-3 group border-l-2 ${cfg.accent} hover:bg-muted/20 transition-colors`}>
      <button
        onClick={() => onToggle(deadline)}
        className="shrink-0"
        data-testid={`toggle-deadline-${deadline.id}`}
      >
        {deadline.completed ? (
          <CheckCircle2 className="w-4 h-4 text-green-600" />
        ) : (
          <Circle className="w-4 h-4 text-muted-foreground/50 hover:text-primary transition-colors" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[9px] font-semibold uppercase tracking-[0.1em] ${cfg.label === 'OFFICIAL' ? 'text-blue-600' : cfg.label === 'MOCK' ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {cfg.label}
          </span>
          {deadline.project && (
            <span className="text-[9px] text-muted-foreground uppercase tracking-wide">
              · {deadline.project.title || deadline.project.type}
            </span>
          )}
        </div>
        <h3 className={`text-sm font-medium ${deadline.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
          {deadline.label}
        </h3>
        {deadline.description && (
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{deadline.description}</p>
        )}
      </div>

      <div className="text-right shrink-0">
        <p className={`text-sm font-semibold font-mono ${variant === 'overdue' ? 'text-destructive' : 'text-foreground'}`}>
          {format(new Date(deadline.dueAt), 'MMM d')}
        </p>
        {!deadline.allDay && (
          <p className="text-[11px] text-muted-foreground font-mono">
            {format(new Date(deadline.dueAt), 'HH:mm')}
          </p>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            data-testid={`deadline-menu-${deadline.id}`}>
            <MoreVertical className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(deadline)} data-testid={`edit-deadline-${deadline.id}`}>
            <Pencil className="w-3.5 h-3.5 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDelete(deadline)} className="text-destructive focus:text-destructive"
            data-testid={`delete-deadline-${deadline.id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DeadlinesPage;
